import {
  applyDarkModeFilter,
  COLOR_WHITE,
  FRAME_STYLE,
  THEME,
  throttleRAF,
} from "@excalidraw/common";
import { isElementLink } from "@excalidraw/element";
import { createPlaceholderEmbeddableLabel } from "@excalidraw/element";
import { getBoundTextElement } from "@excalidraw/element";
import {
  isEmbeddableElement,
  isFrameLikeElement,
  isIframeLikeElement,
  isTextElement,
} from "@excalidraw/element";
import {
  elementOverlapsWithFrame,
  getTargetFrame,
  isElementInViewport,
  shouldApplyFrameClip,
} from "@excalidraw/element";

import { renderElement, renderFrameSurfaceLayer } from "@excalidraw/element";

import { getElementAbsoluteCoords } from "@excalidraw/element";

import {
  animationPivot,
  getAnimationFilter,
  getAnimationOverride,
} from "@excalidraw/element";

import type {
  ElementsMap,
  ExcalidrawFrameLikeElement,
  NonDeletedExcalidrawElement,
} from "@excalidraw/element/types";

import type { AnimatedValues } from "@excalidraw/element";

import {
  EXTERNAL_LINK_IMG,
  ELEMENT_LINK_IMG,
  getLinkHandleFromCoords,
} from "../components/hyperlink/helpers";

import { bootstrapCanvas, getNormalizedCanvasDimensions } from "./helpers";

import type {
  StaticCanvasRenderConfig,
  StaticSceneRenderConfig,
} from "../scene/types";
import type { StaticCanvasAppState, Zoom } from "../types";

const GridLineColor = {
  [THEME.LIGHT]: {
    bold: "#dddddd",
    regular: "#e5e5e5",
  },
  [THEME.DARK]: {
    bold: applyDarkModeFilter("#dddddd"),
    regular: applyDarkModeFilter("#e5e5e5"),
  },
} as const;

export const strokeGrid = (
  context: CanvasRenderingContext2D,
  /** grid cell pixel size */
  gridSize: number,
  /** setting to 1 will disble bold lines */
  gridStep: number,
  scrollX: number,
  scrollY: number,
  zoom: Zoom,
  theme: StaticCanvasRenderConfig["theme"],
  width: number,
  height: number,
  gridStyle: StaticCanvasAppState["gridStyle"] = "lines",
) => {
  const offsetX = (scrollX % gridSize) - gridSize;
  const offsetY = (scrollY % gridSize) - gridSize;

  const actualGridSize = gridSize * zoom.value;

  const spaceWidth = 1 / zoom.value;

  context.save();

  if (gridStyle === "dots") {
    // when zoomed out far enough that per-cell dots would smear into noise,
    // fall back to dots only at the bold (gridStep) intersections — same
    // threshold the line renderer uses to drop regular lines
    const step = actualGridSize < 10 ? gridSize * gridStep : gridSize;
    const dotOffsetX = (scrollX % step) - step;
    const dotOffsetY = (scrollY % step) - step;
    // ponytail: square dots via fillRect — cheaper than an arc() per point and
    // indistinguishable at this size; switch to arc() if we ever want big dots
    const dotSize = Math.min(2 / zoom.value, 2);

    context.fillStyle = GridLineColor[theme].bold;
    for (let x = dotOffsetX; x < dotOffsetX + width + step * 2; x += step) {
      for (let y = dotOffsetY; y < dotOffsetY + height + step * 2; y += step) {
        context.fillRect(x - dotSize / 2, y - dotSize / 2, dotSize, dotSize);
      }
    }

    context.restore();
    return;
  }

  // Offset rendering by 0.5 to ensure that 1px wide lines are crisp.
  // We only do this when zoomed to 100% because otherwise the offset is
  // fractional, and also visibly offsets the elements.
  // We also do this per-axis, as each axis may already be offset by 0.5.
  if (zoom.value === 1) {
    context.translate(offsetX % 1 ? 0 : 0.5, offsetY % 1 ? 0 : 0.5);
  }

  // vertical lines
  for (let x = offsetX; x < offsetX + width + gridSize * 2; x += gridSize) {
    const isBold =
      gridStep > 1 && Math.round(x - scrollX) % (gridStep * gridSize) === 0;
    // don't render regular lines when zoomed out and they're barely visible
    if (!isBold && actualGridSize < 10) {
      continue;
    }

    const lineWidth = Math.min(1 / zoom.value, isBold ? 4 : 1);
    context.lineWidth = lineWidth;
    const lineDash = [lineWidth * 3, spaceWidth + (lineWidth + spaceWidth)];

    context.beginPath();
    context.setLineDash(isBold ? [] : lineDash);
    context.strokeStyle = isBold
      ? GridLineColor[theme].bold
      : GridLineColor[theme].regular;
    context.moveTo(x, offsetY - gridSize);
    context.lineTo(x, Math.ceil(offsetY + height + gridSize * 2));
    context.stroke();
  }

  for (let y = offsetY; y < offsetY + height + gridSize * 2; y += gridSize) {
    const isBold =
      gridStep > 1 && Math.round(y - scrollY) % (gridStep * gridSize) === 0;
    if (!isBold && actualGridSize < 10) {
      continue;
    }

    const lineWidth = Math.min(1 / zoom.value, isBold ? 4 : 1);
    context.lineWidth = lineWidth;
    const lineDash = [lineWidth * 3, spaceWidth + (lineWidth + spaceWidth)];

    context.beginPath();
    context.setLineDash(isBold ? [] : lineDash);
    context.strokeStyle = isBold
      ? GridLineColor[theme].bold
      : GridLineColor[theme].regular;
    context.moveTo(offsetX - gridSize, y);
    context.lineTo(Math.ceil(offsetX + width + gridSize * 2), y);
    context.stroke();
  }
  context.restore();
};

export const frameClip = (
  frame: ExcalidrawFrameLikeElement,
  context: CanvasRenderingContext2D,
  renderConfig: StaticCanvasRenderConfig,
  appState: StaticCanvasAppState,
) => {
  // when the frame itself is translated by an animation override (flipbook
  // page swapped onto the stage), its clip region moves with it; element-level
  // overrides still animate under a fixed clip (wipes/reveals)
  const anim = getAnimationOverride(frame.id);
  const frameX = frame.x + (anim?.dx ?? 0);
  const frameY = frame.y + (anim?.dy ?? 0);
  context.translate(frameX + appState.scrollX, frameY + appState.scrollY);
  context.beginPath();
  if (context.roundRect) {
    context.roundRect(
      0,
      0,
      frame.width,
      frame.height,
      FRAME_STYLE.radius / appState.zoom.value,
    );
  } else {
    context.rect(0, 0, frame.width, frame.height);
  }
  context.clip();
  context.translate(-(frameX + appState.scrollX), -(frameY + appState.scrollY));
};

type LinkIconCanvas = HTMLCanvasElement & { zoom: number };

const linkIconCanvasCache: {
  regularLink: LinkIconCanvas | null;
  elementLink: LinkIconCanvas | null;
} = {
  regularLink: null,
  elementLink: null,
};

const renderLinkIcon = (
  element: NonDeletedExcalidrawElement,
  context: CanvasRenderingContext2D,
  appState: StaticCanvasAppState,
  elementsMap: ElementsMap,
) => {
  if (element.link && !appState.selectedElementIds[element.id]) {
    const [x1, y1, x2, y2] = getElementAbsoluteCoords(element, elementsMap);
    const [x, y, width, height] = getLinkHandleFromCoords(
      [x1, y1, x2, y2],
      element.angle,
      appState,
    );
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    context.save();
    context.translate(appState.scrollX + centerX, appState.scrollY + centerY);
    context.rotate(element.angle);

    const canvasKey = isElementLink(element.link)
      ? "elementLink"
      : "regularLink";

    let linkCanvas = linkIconCanvasCache[canvasKey];

    if (!linkCanvas || linkCanvas.zoom !== appState.zoom.value) {
      linkCanvas = Object.assign(document.createElement("canvas"), {
        zoom: appState.zoom.value,
      });
      linkCanvas.width = width * window.devicePixelRatio * appState.zoom.value;
      linkCanvas.height =
        height * window.devicePixelRatio * appState.zoom.value;
      linkIconCanvasCache[canvasKey] = linkCanvas;

      const linkCanvasCacheContext = linkCanvas.getContext("2d")!;
      linkCanvasCacheContext.scale(
        window.devicePixelRatio * appState.zoom.value,
        window.devicePixelRatio * appState.zoom.value,
      );

      // Seed a sane default so a corrupted color (silently rejected by the
      // canvas) falls back to white instead of a stale fillStyle.
      linkCanvasCacheContext.fillStyle = COLOR_WHITE;
      linkCanvasCacheContext.fillStyle =
        appState.viewBackgroundColor || COLOR_WHITE;

      linkCanvasCacheContext.fillRect(0, 0, width, height);

      if (canvasKey === "elementLink") {
        linkCanvasCacheContext.drawImage(ELEMENT_LINK_IMG, 0, 0, width, height);
      } else {
        linkCanvasCacheContext.drawImage(
          EXTERNAL_LINK_IMG,
          0,
          0,
          width,
          height,
        );
      }

      linkCanvasCacheContext.restore();
    }
    context.globalAlpha = element.opacity / 100;
    context.drawImage(linkCanvas, x - centerX, y - centerY, width, height);
    context.restore();
  }
};
/** Padding around the element bbox for the reveal clip, so strokes,
 * roughness overshoot and shadows aren't sheared off on the static edges. */
const REVEAL_CLIP_PADDING = 32;

/**
 * Applies the current animation override for an element: a context transform
 * (translate/rotate/scale/skew) around the element center, plus canvas filter
 * channels and the wipe-reveal clip. Must run AFTER frame clipping (the clip
 * region stays fixed while content animates under it) and inside the
 * element's context.save()/restore() block, so bound text labels rendered in
 * the same block follow the container.
 */
export const applyAnimationTransform = (
  context: CanvasRenderingContext2D,
  element: { x: number; y: number; width: number; height: number },
  appState: { scrollX: number; scrollY: number },
  anim: AnimatedValues,
) => {
  const [cx, cy] = animationPivot(element, anim, appState);
  context.translate(cx + (anim.dx ?? 0), cy + (anim.dy ?? 0));
  if (anim.angleDelta) {
    context.rotate(anim.angleDelta);
  }
  if (anim.scaleX !== undefined || anim.scaleY !== undefined) {
    context.scale(anim.scaleX ?? 1, anim.scaleY ?? 1);
  }
  if (anim.skewX || anim.skewY) {
    context.transform(
      1,
      Math.tan(anim.skewY ?? 0),
      Math.tan(anim.skewX ?? 0),
      1,
      0,
      0,
    );
  }
  context.translate(-cx, -cy);

  const filter = getAnimationFilter(anim);
  if (filter) {
    context.filter = filter;
  }

  if (anim.revealProgress !== undefined && anim.revealProgress < 1) {
    const progress = Math.max(0, anim.revealProgress);
    const p = REVEAL_CLIP_PADDING;
    const x = element.x + appState.scrollX - p;
    const y = element.y + appState.scrollY - p;
    const w = element.width + p * 2;
    const h = element.height + p * 2;
    context.beginPath();
    switch (anim.revealDir ?? 0) {
      case 1: // from right
        context.rect(x + w * (1 - progress), y, w * progress, h);
        break;
      case 2: // from top
        context.rect(x, y, w, h * progress);
        break;
      case 3: // from bottom
        context.rect(x, y + h * (1 - progress), w, h * progress);
        break;
      case 4: {
        // iris: circle growing from the center; full radius reaches the
        // corners so progress 1 shows everything
        const r = (Math.hypot(w, h) / 2) * progress;
        context.arc(x + w / 2, y + h / 2, Math.max(r, 0), 0, Math.PI * 2);
        break;
      }
      default: // 0: from left
        context.rect(x, y, w * progress, h);
    }
    context.clip();
  }
};

const _renderStaticScene = ({
  canvas,
  rc,
  elementsMap,
  allElementsMap,
  visibleElements,
  scale,
  appState,
  renderConfig,
}: StaticSceneRenderConfig) => {
  if (canvas === null) {
    return;
  }

  const { renderGrid = true, isExporting } = renderConfig;

  // Dark theme is a UI theme here, not a scene filter. Upstream inverts every
  // element color (and the canvas background) in dark mode, which made the
  // canvas disagree with the color you picked in the picker AND with what you
  // export — pick white, get black. On screen we always render the literal
  // colors; only the export path still honors dark mode, via the
  // "Export image → dark mode" toggle that sets appState.theme for that render.
  if (!isExporting) {
    if (appState.theme === THEME.DARK) {
      appState = { ...appState, theme: THEME.LIGHT };
    }
    if (renderConfig.theme === THEME.DARK) {
      renderConfig = { ...renderConfig, theme: THEME.LIGHT };
    }
  }

  const [normalizedWidth, normalizedHeight] = getNormalizedCanvasDimensions(
    canvas,
    scale,
  );

  const context = bootstrapCanvas({
    canvas,
    scale,
    normalizedWidth,
    normalizedHeight,
    theme: appState.theme,
    isExporting,
    viewBackgroundColor: appState.viewBackgroundColor,
  });

  // Apply zoom
  context.scale(appState.zoom.value, appState.zoom.value);

  // Grid
  if (renderGrid) {
    strokeGrid(
      context,
      appState.gridSize,
      appState.gridStep,
      appState.scrollX,
      appState.scrollY,
      appState.zoom,
      renderConfig.theme,
      normalizedWidth / appState.zoom.value,
      normalizedHeight / appState.zoom.value,
      appState.gridStyle,
    );
  }

  const groupsToBeAddedToFrame = new Set<string>();

  visibleElements.forEach((element) => {
    if (
      element.groupIds.length > 0 &&
      appState.frameToHighlight &&
      appState.selectedElementIds[element.id] &&
      (elementOverlapsWithFrame(
        element,
        appState.frameToHighlight,
        elementsMap,
      ) ||
        element.groupIds.find((groupId) => groupsToBeAddedToFrame.has(groupId)))
    ) {
      element.groupIds.forEach((groupId) =>
        groupsToBeAddedToFrame.add(groupId),
      );
    }
  });

  const inFrameGroupsMap = new Map<string, boolean>();

  // Paint frame surfaces (the white "artboard" fill) as a background layer
  // BENEATH every element. Children added to a frame are reordered just below
  // it in z-order, so painting the opaque fill at the frame's own index would
  // hide them — this pre-pass keeps the screen background behind its contents.
  // The frame outline still renders inline (on top) in the main loop below.
  // `!el.hidden` on every pass below: the main canvas already drops hidden
  // elements upstream (scene/Renderer.ts), but the app's own renderers —
  // animation preview, flipbook thumbnails, video/GIF export — build their
  // own `visibleElements` straight from getSceneElements() and bypass it.
  // This is the one point they all route through.
  visibleElements
    .filter(
      (el): el is ExcalidrawFrameLikeElement =>
        isFrameLikeElement(el) && !el.hidden,
    )
    .forEach((frame) => {
      context.save();
      const animationOverride = getAnimationOverride(frame.id);
      if (animationOverride) {
        applyAnimationTransform(context, frame, appState, animationOverride);
      }
      renderFrameSurfaceLayer(frame, context, renderConfig, appState);
      context.restore();
    });

  // Paint visible elements. A hidden element still renders while an animation
  // explicitly reveals it (--> AnimatedValues.revealHidden): that is how a
  // baked flipbook keeps every page but its first out of the static scene and
  // still plays all of them. Layer-hiding is otherwise absolute.
  visibleElements
    .filter(
      (el) =>
        !isIframeLikeElement(el) &&
        (!el.hidden || !!getAnimationOverride(el.id)?.revealHidden),
    )
    .forEach((element) => {
      try {
        const frameId = element.frameId || appState.frameToHighlight?.id;

        if (
          isTextElement(element) &&
          element.containerId &&
          elementsMap.has(element.containerId)
        ) {
          // will be rendered with the container
          return;
        }

        context.save();

        if (
          frameId &&
          appState.frameRendering.enabled &&
          appState.frameRendering.clip
        ) {
          const frame = getTargetFrame(element, elementsMap, appState);
          if (
            frame &&
            shouldApplyFrameClip(
              element,
              frame,
              appState,
              elementsMap,
              inFrameGroupsMap,
            )
          ) {
            frameClip(frame, context, renderConfig, appState);
          }
        }

        const animationOverride = getAnimationOverride(element.id);
        if (animationOverride) {
          applyAnimationTransform(
            context,
            element,
            appState,
            animationOverride,
          );
        }

        renderElement(
          element,
          elementsMap,
          allElementsMap,
          rc,
          context,
          renderConfig,
          appState,
        );

        const boundTextElement = getBoundTextElement(element, elementsMap);
        if (boundTextElement) {
          renderElement(
            boundTextElement,
            elementsMap,
            allElementsMap,
            rc,
            context,
            renderConfig,
            appState,
          );
        }

        context.restore();

        if (!isExporting) {
          renderLinkIcon(element, context, appState, elementsMap);
        }
      } catch (error: any) {
        console.error(
          error,
          element.id,
          element.x,
          element.y,
          element.width,
          element.height,
        );
      }
    });

  // render embeddables on top
  visibleElements
    .filter((el) => isIframeLikeElement(el) && !el.hidden)
    .forEach((element) => {
      try {
        const render = () => {
          renderElement(
            element,
            elementsMap,
            allElementsMap,
            rc,
            context,
            renderConfig,
            appState,
          );

          if (
            isIframeLikeElement(element) &&
            (isExporting ||
              (isEmbeddableElement(element) &&
                renderConfig.embedsValidationStatus.get(element.id) !==
                  true)) &&
            element.width &&
            element.height
          ) {
            const label = createPlaceholderEmbeddableLabel(element);
            renderElement(
              label,
              elementsMap,
              allElementsMap,
              rc,
              context,
              renderConfig,
              appState,
            );
          }
          if (!isExporting) {
            renderLinkIcon(element, context, appState, elementsMap);
          }
        };
        // - when exporting the whole canvas, we DO NOT apply clipping
        // - when we are exporting a particular frame, apply clipping
        //   if the containing frame is not selected, apply clipping
        const frameId = element.frameId || appState.frameToHighlight?.id;

        if (
          frameId &&
          appState.frameRendering.enabled &&
          appState.frameRendering.clip
        ) {
          context.save();

          const frame = getTargetFrame(element, elementsMap, appState);

          if (
            frame &&
            shouldApplyFrameClip(
              element,
              frame,
              appState,
              elementsMap,
              inFrameGroupsMap,
            )
          ) {
            frameClip(frame, context, renderConfig, appState);
          }
          render();
          context.restore();
        } else {
          render();
        }
      } catch (error: any) {
        console.error(error);
      }
    });

  // render pending nodes for flowcharts
  renderConfig.pendingFlowchartNodes?.forEach((element) => {
    try {
      renderElement(
        element,
        elementsMap,
        allElementsMap,
        rc,
        context,
        renderConfig,
        appState,
      );
    } catch (error) {
      console.error(error);
    }
  });
};

/** throttled to animation framerate */
export const renderStaticSceneThrottled = throttleRAF(
  (config: StaticSceneRenderConfig) => {
    _renderStaticScene(config);
  },
);

// Last config the main static canvas rendered with — kept so animation
// playback can redraw directly (see redrawStaticScene). Refreshed on every
// React-path render; any real scene/appState change routes through those and
// updates it.
let lastStaticSceneConfig: StaticSceneRenderConfig | null = null;

/**
 * Static scene is the non-ui canvas where we render elements.
 */
export const renderStaticScene = (
  renderConfig: StaticSceneRenderConfig,
  throttle?: boolean,
) => {
  // only the main canvas (never exporting) may become the redraw target —
  // the preview window and export pipeline call this too, with their own
  // canvases and isExporting configs
  if (!renderConfig.renderConfig.isExporting) {
    lastStaticSceneConfig = renderConfig;
  }
  if (throttle) {
    renderStaticSceneThrottled(renderConfig);
    return;
  }

  _renderStaticScene(renderConfig);
};

/**
 * Immediate redraw for animation playback ticks. Playback never mutates the
 * scene — only the animation overrides change — so routing every tick through
 * scene.triggerUpdate() → React setState → rAF-throttled render both re-runs
 * the whole App reconciliation and delays the paint by a full extra rAF turn
 * (locking playback near 30fps). This draws synchronously inside the ticker's
 * rAF using the last rendered config, re-culling against the overrides so
 * elements animating INTO the viewport from off-screen still appear.
 */
export const redrawStaticScene = () => {
  const config = lastStaticSceneConfig;
  if (!config) {
    return;
  }
  const { appState, elementsMap } = config;
  const visibleElements: NonDeletedExcalidrawElement[] = [];
  for (const element of elementsMap.values()) {
    if (
      isElementInViewport(
        element,
        appState.width,
        appState.height,
        {
          zoom: appState.zoom,
          offsetLeft: appState.offsetLeft,
          offsetTop: appState.offsetTop,
          scrollX: appState.scrollX,
          scrollY: appState.scrollY,
        },
        elementsMap,
      )
    ) {
      visibleElements.push(element);
    }
  }
  _renderStaticScene({ ...config, visibleElements });
};
